import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Money } from '@clari/ui'
import { businessApi, type PublicRevenueFigure } from '@/lib/business-api'

const CATEGORIES = [
  { key: 'subscriptions', field: 'subscriptions_cents' },
  { key: 'interchange', field: 'interchange_cents' },
  { key: 'fx', field: 'fx_margin_cents' },
  { key: 'paylink', field: 'paylink_cents' },
] as const satisfies ReadonlyArray<{ key: string; field: keyof PublicRevenueFigure }>

/**
 * The real numbers (USP #7). Shows the latest published month's revenue
 * breakdown; falls back to the "coming soon" copy until a month is published.
 * Fetched anonymously from the public /business-model/figures endpoint.
 */
export function RevenueFigures() {
  const { t, i18n } = useTranslation('business_model')
  const { data } = useQuery({
    queryKey: ['public-revenue'],
    queryFn: () => businessApi.getRevenue(),
  })
  const figure = data?.figure ?? null

  return (
    <section className="mt-16 rounded-3xl border border-border bg-bg-subtle p-6">
      <h2 className="text-xl font-medium">{t('figures.title')}</h2>

      {figure ? (
        <>
          <p className="mt-1 text-sm text-text-subtle">
            {t('figures.period', { period: figure.period })}
          </p>
          <ul className="mt-4 flex flex-col gap-3">
            {CATEGORIES.map(({ key, field }) => {
              const cents = figure[field] as number
              const share =
                figure.total_cents > 0 ? Math.round((cents / figure.total_cents) * 100) : 0
              return (
                <li key={key} className="flex items-center justify-between gap-4">
                  <span className="text-text-muted">{t(`earn.${key}.label`)}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-sm text-text-subtle">
                      {t('figures.share', { share })}
                    </span>
                    <Money amountCents={cents} currency={figure.currency} locale={i18n.language} />
                  </span>
                </li>
              )
            })}
            <li className="flex items-center justify-between gap-4 border-t border-border pt-3 font-medium">
              <span>{t('figures.total')}</span>
              <Money
                amountCents={figure.total_cents}
                currency={figure.currency}
                locale={i18n.language}
              />
            </li>
          </ul>
          <p className="mt-4 text-sm text-text-subtle">{t('figures.audit_note')}</p>
        </>
      ) : (
        <>
          <p className="mt-2 font-mono text-text-muted">{t('figures.placeholder')}</p>
          <p className="mt-1 text-sm text-text-subtle">{t('figures.audit_note')}</p>
        </>
      )}
    </section>
  )
}
